import { Box, NumberInput, SimpleGrid, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { IFileState } from 'components/files/upload/types';
import UploadFiles from 'components/files/upload/UploadFiles';
import FormInputGroup from 'components/forms/FormInputGroup';
import { FormProps } from 'components/forms/FormProps';
import FormWrapper from 'components/forms/FormWrapper';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import P2Regular from 'components/typography/P2Regular';
import useLoadingAction from 'hooks/use-loading-action';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useMemo, useState } from 'react';

export interface PriceOfferProvideFormData {
  finalPriceLabor: number | '';
  finalPriceMaterial: number | '';
  fileId: string;
  note: string;
}

/**
 * Form used by providers to submit or update a price offer.
 */
export default function PriceOfferProvideForm({
  initialValues = {
    finalPriceLabor: '',
    finalPriceMaterial: '',
    fileId: '',
    note: '',
  },
  onSubmit = noop,
}: FormProps<PriceOfferProvideFormData>) {
  const { isTablet } = useResponsiveDesign();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const [files, setFiles] = useState<IFileState[]>([]);

  const form = useForm<PriceOfferProvideFormData>({
    initialValues,
    validate: {
      finalPriceLabor: (value) => required(value)?.message ?? null,
      finalPriceMaterial: (value) => required(value)?.message ?? null,
      fileId: createValidator([required]),
    },
  });

  const uploading = useMemo(() => files.some(({ finalized }) => !finalized), [files]);

  return (
    <FormWrapper
      onSubmit={form.onSubmit(submit)}
      loading={loading || uploading}
      primaryButtonText="Odovzdať"
      primaryIcon={<IconCheck stroke="1.5" height={24} width={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Cenová ponuka">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <NumberInput
            label="Cena práce"
            placeholder="0,00"
            precision={2}
            min={0}
            size="lg"
            rightSection="€"
            {...form.getInputProps('finalPriceLabor')}
          />
          <NumberInput
            label="Cena materiálu"
            placeholder="0,00"
            precision={2}
            min={0}
            size="lg"
            rightSection="€"
            {...form.getInputProps('finalPriceMaterial')}
          />
        </SimpleGrid>
        <Box pt={12}>
          <P2Regular pb={4} color="gray.6">
            Príloha
          </P2Regular>
          <UploadFiles
            onChange={(nextFiles = []) => {
              setFiles(nextFiles);
              const file = nextFiles.find((item) => item.finalized && item.fileId);
              form.setFieldValue('fileId', file?.fileId ?? '');
            }}
            error={form.errors.fileId}
          />
        </Box>
        <Textarea
          pt={12}
          label="Poznámka"
          placeholder="Nepovinná poznámka do záznamu o odovzdaní"
          minRows={3}
          autosize
          size="lg"
          {...form.getInputProps('note')}
        />
      </FormInputGroup>
    </FormWrapper>
  );
}
